import React, {useCallback, useContext, useEffect, useMemo, useState} from 'react';
import {useSelector} from 'react-redux';
import {useNavigate, useParams} from 'react-router-dom';

import {ProfileMain} from 'src/components/profile-tab-structure/profile-main';
import {useConfirmationChunk} from 'src/contexts/confirmation-context';
import {MessageContext} from 'src/contexts/message-context';
import {ValidationContextProvider} from 'src/contexts/validation-context/validation-context-provider';
import {useAdminAccessRight} from 'src/hooks/access-rights';
import {selectDeprecatedAccessModulesMap} from 'src/hooks/deprecatedAccessModules/services/deprecated-access-modules-slice';
import {useSupportedLicenseTypes} from 'src/hooks/dictionaries';
import {useEffectWithDeps} from 'src/hooks/useEffectWithDeps';
import {useEventTracking} from 'src/hooks/useEventTracking';
import {selectAccountDetails} from 'src/modules/profile/account/services/account-page-slice';
import {LICENSE_AND_FEATURES_SUB_TABS} from 'src/modules/profile/account/tabs/license-and-features/constants';
import {
    AccessToModulesSection,
    useLicenseDataSets,
    usePlatformFeatureModules,
    usePlatformFeatures,
} from 'src/modules/profile/account/tabs/license-and-features/features-and-limits-tab/sections/access-to-modules';
import {
    LicenseUsageLimitsView,
    useCreateLicenseLimits,
    useDefaultLicenseLimits,
} from 'src/modules/usage-limits';
import {isDeepEqual} from 'src/utils/isDeepEqual';

import Button from '@pb/kit-component-button';
import {Group, Item} from '@pb/kit-component-group';
import Typography from '@pb/kit-component-typography';

import {LicenseSection, useLicense} from './sections/license';

import type {MessageContextType} from 'src/contexts/message-context';


const CreateLicensePage = () => {
    const params = useParams<{templateLicenseId?: string, accountId: string}>();
    const accountId = params.accountId ? +params.accountId : -1;
    const templateLicenseId = params.templateLicenseId ? +params.templateLicenseId : undefined;
    const navigate = useNavigate();
    const {hierarchy} = useSelector(selectAccountDetails);

    const [createBusy, setCreateBusy] = useState(false);
    const {setErrorMessage} = useContext<MessageContextType>(MessageContext);
    const {licenseCreateSubmit, licenseCreateChange} = useEventTracking();


    const {setIsChunkChanged, proceed} = useConfirmationChunk({
        chunkName: 'create-license',
        onProceed: createdLicenseId => {

            navigate(`/profile/account/${accountId}/license-features/${createdLicenseId}/${LICENSE_AND_FEATURES_SUB_TABS.FEATURES_AND_LIMITS.name}`);
        },
    });

    const {
        licenseLoading,
        license,
        setLicense,
        createLicense,
        isInitialLicense,
    } = useLicense({accountId, templateLicenseId, accountEntityType: hierarchy?.current?.entityTypeCode || ''});

    const {
        isLoading: isDefaultLicenseLimitsLoading,
        loadingError: defaultLicenseLimitsError,
        data: defaultLicenseLimits,
        loadData: loadDefaultLicenseLimits,
    } = useDefaultLicenseLimits({licenseType: license.licenseType});

    const {
        isLoading: isUsageLimitsLoading,
        loadingError: usageLimitsLoadingError,
        data: usageLimits,
        setData: setUsageLimits,
        isChanged: isChangedUsageLimits,
        saveChanges: saveUsageLimits,
        loadData: loadUsageLimits,
    } = useCreateLicenseLimits({templateLicenseId, defaultLicenseLimits});

    const {
        loading: dataSetsLoading,
        dataSets,
        setDataSets,
        dataSetsTree,
        isInitialDataSets,
        isDefaultDataSets,
        onSaveDataSets,
        onResetDataSets,
    } = useLicenseDataSets({licenseType: license.licenseType, licenseId: templateLicenseId});

    const {
        loading: platformFeatureModulesLoading,
        platformFeatureModulesTree,
        platformFeatureModules,
        setPlatformFeatureModules,
        isInitialPlatformFeatureModules,
        onSavePlatformFeatureModules,
        isDefaultPlatformFeatureModules,
        onResetPlatformFeatureModules,
    } = usePlatformFeatureModules({
        accountId,
        licenseType: license.licenseType,
        licenseId: templateLicenseId,
    });

    const {
        loading: platformFeaturesLoading,
        pluginsAppsTree,
        exportOptionsTree,
        platformFeatures,
        setPlatformFeatures,
        isInitialPlatformFeatures,
        onSavePlatformFeatures,
        isDefaultPlatformFeatures,
        onResetPlatformFeatures,
    } = usePlatformFeatures({
        accountId,
        licenseType: license.licenseType,
        licenseId: templateLicenseId,
    });

    const {
        isLoading: supportedLicenseTypesLoading,
        loadingError: supportedLicenseTypesError,
        data: supportedLicenseTypes,
    } = useSupportedLicenseTypes({accountId});
    const deprecatedAccessModulesMap = useSelector(selectDeprecatedAccessModulesMap);

    useEffectWithDeps((prev, current) => {
        if (!isDeepEqual({prev, current})) {
            loadDefaultLicenseLimits();
        }
    }, license.licenseType);

    useEffectWithDeps((prev, current) => {
        if (!isDeepEqual({prev, current})) {
            loadUsageLimits();
        }
    }, defaultLicenseLimits);

    useEffect(() => {
        setIsChunkChanged([
            !isInitialLicense,
            isChangedUsageLimits,
            !isInitialDataSets,
            !isInitialPlatformFeatureModules,
            !isInitialPlatformFeatures,
        ].some(Boolean));
    }, [
        setIsChunkChanged,
        isInitialLicense, isChangedUsageLimits, isInitialDataSets,
        isInitialPlatformFeatureModules, isInitialPlatformFeatures,
    ]);

    const onCancel = useCallback(() => {
        licenseCreateSubmit({submit: false});
        navigate(-1);
    }, [licenseCreateSubmit, navigate]);


    const onCreateLicense = useCallback(() => {
        licenseCreateSubmit({submit: true});

        setCreateBusy(true);
        createLicense(async (licenseId: number) => {
            const callResults = [];

            if (isChangedUsageLimits || templateLicenseId) {
                callResults.push(await saveUsageLimits(licenseId));
            }

            if (!dataSets.length) {
                if (!isInitialPlatformFeatureModules || templateLicenseId) {
                    callResults.push(await onSavePlatformFeatureModules(licenseId));
                }

                if (!isInitialDataSets || templateLicenseId) {
                    callResults.push(await onSaveDataSets(licenseId));
                }
            } else {
                if (!isInitialDataSets || templateLicenseId) {
                    callResults.push(await onSaveDataSets(licenseId));
                }

                if (!isInitialPlatformFeatureModules || templateLicenseId) {
                    callResults.push(await onSavePlatformFeatureModules(licenseId));
                }
            }

            if (!isInitialPlatformFeatures || templateLicenseId) {
                callResults.push(await onSavePlatformFeatures(licenseId));
            }

            const errors = [...new Set(callResults.filter(Boolean))];
            setCreateBusy(false);

            if (errors.length) {
                setErrorMessage(errors.join(' '));
            } else {
                proceed(licenseId);
            }
        }, error => {
            setCreateBusy(false);
            setErrorMessage(error);
        }, !dataSets.length && !platformFeatureModules.length);
    }, [
        templateLicenseId,
        createLicense,
        dataSets,
        platformFeatureModules,
        isChangedUsageLimits,
        isInitialDataSets,
        isInitialPlatformFeatureModules,
        isInitialPlatformFeatures,
        saveUsageLimits,
        onSaveDataSets,
        onSavePlatformFeatureModules,
        onSavePlatformFeatures,
        licenseCreateSubmit,
        proceed,
        setErrorMessage,
    ]);

    const onResetModules = useCallback(() => {
        onResetDataSets();
        onResetPlatformFeatureModules();
        onResetPlatformFeatures();
    }, [onResetDataSets, onResetPlatformFeatureModules, onResetPlatformFeatures]);

    const isDefaultModules = useMemo(() => (
        isDefaultDataSets && isDefaultPlatformFeatureModules && isDefaultPlatformFeatures
    ), [isDefaultDataSets, isDefaultPlatformFeatureModules, isDefaultPlatformFeatures]);

    const onFieldChange = useCallback((parameterName: string) => {
        licenseCreateChange({parameterName});
    }, [licenseCreateChange]);

    const {canEditAccountLimits} = useAdminAccessRight();
    const canEditLimits = canEditAccountLimits(accountId);

    return (
        <ProfileMain header={(
            <Item grow={1}>
                <Group justify="space-between">
                    <Typography fontWeight="semi-bold" variant="heading-level-3">
                        {templateLicenseId ? 'New License From Existing' : 'New License'}
                    </Typography>
                    <Group>
                        <Button variant="Type.SECONDARY" onClick={onCancel}>
                            Cancel
                        </Button>
                        <Button
                            busy={createBusy}
                            variant="Type.PRIMARY"
                            onClick={onCreateLicense}>
                            Create License
                        </Button>
                    </Group>
                </Group>
            </Item>
        )}>
            <LicenseSection
                loadingError={supportedLicenseTypesError}
                supportedLicenseTypes={supportedLicenseTypes}
                disableLicenseTypeSelect={!!templateLicenseId}
                licenseLoading={licenseLoading || supportedLicenseTypesLoading}
                license={license}
                onChangeLicense={setLicense}
                onFieldChange={onFieldChange}
            />
            <LicenseUsageLimitsView
                licenseType={license.licenseType}
                isDefaultUsageLimits={isDeepEqual({prev: defaultLicenseLimits, current: usageLimits})}
                disabled={!canEditLimits}
                loading={isUsageLimitsLoading || isDefaultLicenseLimitsLoading}
                loadingError={usageLimitsLoadingError || defaultLicenseLimitsError}
                usageLimits={usageLimits}
                defaultUsageLimits={defaultLicenseLimits}
                onChange={setUsageLimits}
                onFieldChange={onFieldChange}
            />
            <AccessToModulesSection
                licenseType={license.licenseType}
                isDefaultModules={isDefaultModules}
                onResetModules={onResetModules}
                dataSetsLoading={dataSetsLoading}

                dataSets={dataSets}
                dataSetsTree={dataSetsTree}
                setDataSets={setDataSets}
                onFieldChange={onFieldChange}

                platformFeatureModulesLoading={platformFeatureModulesLoading}
                platformFeatureModulesTree={platformFeatureModulesTree}
                platformFeatureModules={platformFeatureModules
                    .filter(feature => !(deprecatedAccessModulesMap?.[feature]))}
                setPlatformFeatureModules={setPlatformFeatureModules}

                platformFeaturesLoading={platformFeaturesLoading}
                pluginsAppsTree={pluginsAppsTree}
                exportOptionsTree={exportOptionsTree}
                platformFeatures={platformFeatures}
                setPlatformFeatures={setPlatformFeatures}
            />
        </ProfileMain>
    );
};

export const ValidatedCreateLicensePage = () => (
    <ValidationContextProvider>
        <CreateLicensePage/>
    </ValidationContextProvider>
);
